// Live preview: renders the form model with @askdown/html's renderForm and
// enhances it with the runtime (paging, branching, validation). Re-renders
// preserve the scroll position, the current page and the answers entered so
// far. Submitting shows the runtime's "Preview — not submitted" confirmation.
import { renderMarkdown } from '@askdown/core';
import { readAnswers, renderForm } from '@askdown/html';
import { initForm } from '@askdown/html/runtime';
import { h, clear } from './dom.js';

const noop = () => {};

/**
 * @param {HTMLElement} container
 * @param {{ onQuestionClick?: (id: string) => void }} options
 */
export function createPreview(container, options = {}) {
  const scroller = h('div.preview-scroll');
  const note = h('div.preview-note', { role: 'status' }, 'Preview: answers are validated but never submitted.');
  const mount = h('div.preview-mount');
  const empty = h('div.preview-empty', 'Add a question in the editor to see it here.');
  scroller.append(note, mount);
  container.append(scroller);

  /** @type {import('@askdown/html/runtime').FormController | null} */
  let controller = null;
  let formEl = null;
  let currentForm = null;
  let renders = 0;

  function captureState() {
    if (!formEl || !controller || !currentForm) return null;
    let answers;
    try {
      answers = readAnswers(currentForm, new FormData(formEl)).answers;
    } catch {
      answers = {};
    }
    const hasAnswers = Object.values(answers).some(
      (v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0),
    );
    return { answers: hasAnswers ? answers : undefined, page: controller.currentPage(), scrollTop: scroller.scrollTop };
  }

  function restorePage(page) {
    if (!controller || !formEl || page <= 0) return;
    if (page >= formEl.querySelectorAll('.askdown-page').length) return;
    // Don't steal the caret from the editor on every keystroke.
    controller.goTo(page, { focus: false });
  }

  function render(form) {
    const prev = captureState();
    const active = document.activeElement;
    if (controller) {
      controller.destroy();
      controller = null;
    }
    clear(mount);
    currentForm = form;
    renders++;
    const hasItems = form.pages.some((p) => p.items.length > 0) || form.title;
    if (!hasItems) {
      mount.append(empty);
      formEl = null;
      return;
    }
    const html = renderForm(form, {
      preview: true,
      idPrefix: `pv${renders % 2}`,
      values: prev && prev.answers ? prev.answers : undefined,
    });
    mount.innerHTML = html;
    formEl = mount.querySelector('form[data-askdown]');
    if (!formEl) return;
    // The preview lives in a scroll pane; the runtime must never scroll the window.
    formEl.scrollIntoView = noop;
    controller = initForm(formEl, {
      onSubmit() {
        showSubmitted(form);
      },
    });
    if (prev) {
      restorePage(prev.page);
      scroller.scrollTop = prev.scrollTop;
    }
    if (active && active !== document.body && document.activeElement !== active && active.isConnected) {
      active.focus({ preventScroll: true });
    }
  }

  function showSubmitted(form) {
    if (!formEl) return;
    const confirmation = h('div.askdown-confirmation.askdown-confirmation--preview', { role: 'status', tabindex: '-1' });
    confirmation.append(h('p.askdown-preview-note', 'Preview — this response was not submitted.'));
    const body = h('div.askdown-confirmation-body');
    body.innerHTML = form.settings?.confirmation
      ? renderConfirmationBody(form.settings.confirmation)
      : '<p>Your response has been recorded.</p>';
    const again = h(
      'button.askdown-button.askdown-button--secondary',
      {
        type: 'button',
        onClick: () => {
          render(currentForm);
          scroller.scrollTop = 0;
        },
      },
      'Fill in again',
    );
    confirmation.append(body, again);
    for (const page of formEl.querySelectorAll('.askdown-page')) page.hidden = true;
    const actions = formEl.querySelector('.askdown-actions');
    if (actions) actions.hidden = true;
    formEl.querySelector('.askdown-pages')?.append(confirmation);
    confirmation.focus({ preventScroll: true });
  }

  function renderConfirmationBody(markdown) {
    return renderMarkdown(markdown, { headingOffset: 1 });
  }

  mount.addEventListener('click', (e) => {
    if (!options.onQuestionClick) return;
    const label = e.target.closest('.askdown-label');
    if (!label) return;
    const q = label.closest('.askdown-question');
    if (q && q.dataset.questionId) options.onQuestionClick(q.dataset.questionId);
  });

  return {
    el: scroller,
    render,
    getController: () => controller,
    getFormEl: () => formEl,
    reset() {
      if (currentForm) {
        controller?.destroy();
        controller = null;
        clear(mount);
        formEl = null;
        const f = currentForm;
        currentForm = null;
        render(f);
      }
    },
  };
}
